import './video-bar.component.scss';
import './video-bar.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LoginType } from '../../../auth/models/auth.model';
import { selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, initializeCustomer } from '../../../auth/store/auth.thunk';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { ContentSort, VideoBarRequest } from '../../models/video-bar.interface';
import {
  selectVideoBarById,
  selectVideoBarsLoading,
  selectVideoById,
} from '../../store/content.selectors';
import { loadVideoBar } from '../../store/content.thunk';
import { Ghost } from './ghost/ghost.component';
import { VideoBarGrid } from './video-bar-grid/video-bar-grid.component';
import { VideoBarSlider } from './video-bar-slider/video-bar-slider.component';

export interface VideoBarProps {
  title: string;
  description: string;
  tags: string;
  sort: ContentSort;
  maxVideos: number;
  allVideos: boolean;
  showViewAll: boolean;
  showBadge: boolean;
  showVideoTitle: boolean;
  showVideoDate: boolean;
  showVideoArtist: boolean;
  showCardDropShadow: boolean;
  maxWidth: boolean;
  display: 'always' | 'has_access' | 'no_access';
  badgeText: string;
  displayType: 'scroll' | 'grid';
  titleSize: string;
  loggedIn: boolean;
  // CSS VARIABLES
  cardBorderRadius: number;
  columnCount: number;
  textColor: string;
  background: string;
  badgeColor: string;
  badgeBackground: string;
  loadingBackground: string;
  verticalPadding: string;
  horizontalPadding: string;
  thumbnailHeight: string;
}

const VideoBarContent: FunctionComponent<VideoBarProps> = ({
  title,
  description,
  tags,
  sort,
  maxVideos,
  allVideos,
  showViewAll,
  showBadge,
  showVideoTitle,
  showVideoDate,
  showVideoArtist,
  showCardDropShadow,
  maxWidth,
  display,
  badgeText,
  displayType,
  titleSize,
  loggedIn,
  cardBorderRadius,
  columnCount,
  textColor,
  background,
  badgeColor,
  badgeBackground,
  loadingBackground,
  verticalPadding,
  horizontalPadding,
  thumbnailHeight,
}: VideoBarProps) => {
  const cssVars = {
    '--cardBorderRadius': cardBorderRadius,
    '--columnCount': columnCount,
    '--textColor': textColor,
    '--background': background,
    '--badgeColor': badgeColor,
    '--badgeBackground': badgeBackground,
    '--loadingBackground': loadingBackground,
    '--verticalPadding': verticalPadding,
    '--horizontalPadding': horizontalPadding,
    '--thumbnailHeight': thumbnailHeight,
  };

  const generateVideoBarId = (): string => {
    // Create a unique ID based on component inputs to identify this specific video bar instance
    const inputs = [
      title || '',
      description || '',
      tags || '',
      sort || '',
      maxVideos || '',
      allVideos || false,
      displayType || '',
      columnCount || '',
      Math.random().toString(36).substr(2, 9),
    ].join('|');

    return btoa(inputs)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substr(0, 16);
  };
  const instanceId = generateVideoBarId();

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectVideoBarsLoading);
  const videoBarResp = useAppSelector(selectVideoBarById(instanceId));
  const { content: videoBar } = videoBarResp || {};

  const request: VideoBarRequest = {
    title,
    description,
    tags,
    sort,
    allVideos,
    maxVideos,
    badgeText,
    titleSize,
    displayType,
    columnCount,
    showViewAll,
    showBadge,
    showVideoTitle,
    showVideoDate,
    showVideoArtist,
    showCardDropShadow,
    maxWidth: maxWidth,
    instanceId,
  };

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());
    dispatch(loadVideoBar(request));
  }, [dispatch]);

  const shouldDisplay = (): boolean => {
    return (
      !display ||
      display === 'always' ||
      (display === 'has_access' && isAuthenticated) ||
      (display === 'no_access' && !isAuthenticated)
    );
  };

  const shouldNotDisplay = (): boolean => !shouldDisplay();

  const getWrapperClassNames = () => {
    const classes = [];
    if (shouldNotDisplay()) classes.push('single-hidden');
    if (!isAuthenticated) classes.push('no-padding');
    return classes.join(' ');
  };

  const getContentSectionClassNames = () => {
    const classes = [];
    if (maxWidth && displayType === 'grid') classes.push('page-width');
    if (displayType === 'scroll') classes.push('single-display-scroll');
    return classes.join(' ');
  };

  return (
    <div class={`single-video-bar-wrapper ${getWrapperClassNames()}`} style={cssVars}>
      {(loading || !videoBar || videoBar.length === 0) && (
        <Ghost
          displayType={displayType}
          title={title}
          titleSize={titleSize}
          description={description}
          showVideoTitle={showVideoTitle}
          showVideoArtist={showVideoArtist}
          maxWidth={maxWidth}
          loadingBackground={loadingBackground}
        />
      )}

      {videoBar && videoBar.length > 0 && (
        <div class={`single-content-section ${getContentSectionClassNames()}`}>
          {title && <h2 class={`title ${titleSize} single-video-collection-title`}>{title}</h2>}
          {description && (
            <div
              class="single-video-collection-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}

          {displayType === 'grid' && (
            <VideoBarGrid
              request={request}
              videoBar={videoBar}
              showViewAll={showViewAll}
              showBadge={showBadge}
              showVideoTitle={showVideoTitle}
              showVideoDate={showVideoArtist}
              showVideoArtist={showVideoArtist}
              showCardDropShadow={showCardDropShadow}
              badgeText={badgeText}
            />
          )}

          {displayType === 'scroll' && (
            <VideoBarSlider
              videoBar={videoBar}
              columnCount={columnCount}
              displayType={displayType}
              showBadge={showBadge}
              showVideoTitle={showVideoTitle}
              showVideoDate={showVideoArtist}
              showVideoArtist={showVideoArtist}
              showCardDropShadow={showCardDropShadow}
              badgeText={badgeText}
            />
          )}
        </div>
      )}
    </div>
  );
};

export const VideoBarComponent: FunctionComponent<VideoBarProps> = (props) => {
  return (
    <Provider store={store}>
      <VideoBarContent {...props} />
    </Provider>
  );
};
